/**
 * CreditCardVisual — Cartão de crédito visual dinâmico (TEMA CLARO) que
 * reflete em tempo real os dados preenchidos pelo pagador no checkout
 * transparente.
 *
 * Tamanho real (~330×210). Frente clara (branco com cantos decorativos
 * em verde menta) e verso claro com tarja escura sutil + CVV. Vira em 3D
 * quando o foco vai para o CVV.
 *
 * RELEASE: 2026-06-21 v2.0.0 — Redesign em tema claro (white card,
 *          chip dourado, accents emerald, sombra suave, tamanho real).
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial (cartão black premium).
 */
import { VisaIcon } from './brands/Visa';
import { MastercardIcon } from './brands/Mastercard';
import { AmexIcon } from './brands/Amex';
import { EloIcon } from './brands/Elo';
import type { CardPreview, CardBrand } from '../CardPaymentForm';

interface Props {
  preview: CardPreview;
  className?: string;
}

/** Retorna o componente SVG da bandeira detectada. */
function BrandLogo({ brand }: { brand: CardBrand }) {
  const cls = 'h-7 w-auto';
  switch (brand) {
    case 'visa':
      return <VisaIcon className={cls} />;
    case 'master':
      return <MastercardIcon className={cls} />;
    case 'amex':
      return <AmexIcon className={cls} />;
    case 'elo':
      return <EloIcon className={cls} />;
    default:
      return <div className="h-7 w-10 rounded bg-slate-100" aria-hidden />;
  }
}

/** Formata o número em 4 grupos de 4, substituindo bullets pelos dígitos digitados. */
function formatNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 16);
  const groups = ['••••', '••••', '••••', '••••'];
  for (let i = 0; i < digits.length; i++) {
    const g = Math.floor(i / 4);
    const p = i % 4;
    if (g >= groups.length) break;
    const arr = groups[g].split('');
    arr[p] = digits[i];
    groups[g] = arr.join('');
  }
  return groups.join(' ');
}

export function CreditCardVisual({ preview, className = '' }: Props) {
  const isBack = preview.focus === 'cvv';
  const numberDisplay = formatNumber(preview.number);
  const holderDisplay = (preview.holder || 'NOME DO CLIENTE').toUpperCase();
  const expiryDisplay = preview.expiry || 'MM/AA';

  // Glow sutil quando o respectivo campo está focado.
  const numberFocused = preview.focus === 'number';
  const holderFocused = preview.focus === 'holder';
  const expiryFocused = preview.focus === 'expiry';

  return (
    <div
      className={`relative w-[330px] h-[210px] ${className}`}
      style={{ perspective: '1200px' }}
      aria-hidden
    >
      <div
        className="relative w-full h-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: isBack ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* ============================== FRENTE ============================== */}
        <div
          className="absolute inset-0 rounded-3xl p-7 bg-white border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.08)] flex flex-col justify-between overflow-hidden"
          style={{ backfaceVisibility: 'hidden' }}
        >
          {/* Cantos decorativos */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gray-100/60 rounded-full -ml-12 -mb-12" />

          {/* Topo: chip + bandeira */}
          <div className="relative z-10 flex items-start justify-between">
            <div className="w-12 h-9 rounded-md bg-gradient-to-br from-amber-200 to-amber-400 shadow-inner flex items-center justify-center">
              <div className="w-9 h-6 border border-amber-700/20 rounded-sm grid grid-cols-3 gap-px p-0.5">
                <div className="bg-amber-700/20" />
                <div className="bg-amber-700/20" />
                <div className="bg-amber-700/20" />
                <div className="bg-amber-700/20" />
                <div className="bg-amber-700/20" />
                <div className="bg-amber-700/20" />
              </div>
            </div>
            <div className="min-h-[28px] flex items-center">
              <BrandLogo brand={preview.brand} />
            </div>
          </div>

          {/* Número do cartão */}
          <div
            className={`relative z-10 font-mono text-xl font-bold tracking-[0.22em] transition-all duration-200 ${
              numberFocused
                ? 'text-gray-900 drop-shadow-[0_0_8px_rgba(16,185,129,0.25)]'
                : preview.number
                  ? 'text-gray-800'
                  : 'text-gray-300'
            }`}
          >
            {numberDisplay}
          </div>

          {/* Rodapé: titular + validade */}
          <div className="relative z-10 flex justify-between items-end gap-3">
            <div
              className={`flex-1 min-w-0 transition-all ${
                holderFocused ? 'drop-shadow-[0_0_6px_rgba(16,185,129,0.25)]' : ''
              }`}
            >
              <p className="text-[9px] text-gray-400 uppercase font-bold tracking-widest mb-1">
                Nome do titular
              </p>
              <p
                className={`text-sm font-bold uppercase tracking-tight truncate ${
                  preview.holder ? 'text-gray-900' : 'text-gray-300'
                }`}
              >
                {holderDisplay}
              </p>
            </div>
            <div
              className={`text-right transition-all ${
                expiryFocused ? 'drop-shadow-[0_0_6px_rgba(16,185,129,0.25)]' : ''
              }`}
            >
              <p className="text-[9px] text-gray-400 uppercase font-bold tracking-widest mb-1">
                Expiração
              </p>
              <p
                className={`text-sm font-bold tracking-tight font-mono ${
                  preview.expiry ? 'text-gray-900' : 'text-gray-300'
                }`}
              >
                {expiryDisplay}
              </p>
            </div>
          </div>
        </div>

        {/* ============================== VERSO ============================== */}
        <div
          className="absolute inset-0 rounded-3xl bg-white border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.08)] overflow-hidden"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          {/* Tarja magnética */}
          <div className="w-full h-10 bg-slate-800 mt-6" />
          {/* Faixa CVV */}
          <div className="px-7 mt-5">
            <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold mb-1">
              CVV
            </p>
            <div className="bg-gray-50 border border-gray-200 h-9 rounded-md flex items-center justify-end pr-3">
              <span className="font-mono text-gray-800 text-sm tracking-[0.35em]">
                •••
              </span>
            </div>
            <p className="text-[10px] text-gray-400 mt-3">
              3 dígitos no verso do cartão.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
